import { Doc } from '@dadohr/shared/domainTypes/document';
import { PINBOARD_INCLUDED_FIELDS } from '@dadohr/shared/domainTypes/integrations/pingboard';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  DefaultDadoField,
  UserImportDataSourcePingboard,
  UserImportDataSourceStatus,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { Button, DialogActions, DialogContent } from '@material-ui/core';
import { useState } from 'react';
import shortid from 'shortid';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../components/SomethingWentWrong';
import { usePromise } from '../../../../../../hooks/usePromise';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { setDoc } from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import {
  getPingboardFields,
  usePingboardConfig
} from '../../../../../Org/pages/Integrations/integrations/pingboard/service';
import { CustomImportMappingFormWithAutocomplete } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem, IntegrationNotConfigured } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.PINGBOARD;

const DEFAULT_MAPPINGS: UserImportMapping[] = [
  {
    dadoKey: 'firstName',
    otherKey: 'first_name'
  },
  {
    dadoKey: 'lastName',
    otherKey: 'last_name'
  },
  {
    dadoKey: 'email',
    otherKey: 'email'
  },
  {
    dadoKey: 'hireDate',
    otherKey: 'start_date'
  },
  {
    dadoKey: DefaultDadoField.DEPARTMENT,
    otherKey: PINBOARD_INCLUDED_FIELDS.DEPARTMENT
  },
  {
    dadoKey: DefaultDadoField.LOCATION,
    otherKey: PINBOARD_INCLUDED_FIELDS.LOCATION
  }
];

export const PingboardSetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { id: userId } = useCurrentUser();
  const [config, loading, error] = usePingboardConfig(orgId, userId);
  const isSetup = !!(config?.data.clientId && config?.data.clientSecret);
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [mappings, setMappings] = useState<UserImportMapping[]>(
    DEFAULT_MAPPINGS
  );

  const [pingboardFields, loadingFields, errorFields] = usePromise(async () => {
    const result = await getPingboardFields(orgId);
    return result;
  });

  if (loading || loadingFields) {
    return (
      <DialogContent>
        <Loader height={250} />
      </DialogContent>
    );
  }
  if (error || errorFields) {
    return <SomethingWentWrong height={150} />;
  }
  if (!config) {
    return null;
  }

  if (!isSetup) {
    return (
      <IntegrationNotConfigured integration={META.name} onClose={onClose} />
    );
  }

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>

        {pingboardFields && (
          <CustomImportMappingFormWithAutocomplete
            additionalCompositeFields={['$FULL_NAME$']}
            userFields={userFields}
            integrationName={META.name}
            name={t('Default Field Mapping')}
            mappings={mappings}
            onChange={(mappings) => setMappings(mappings)}
            fields={pingboardFields.map((f) => ({
              name: f.fieldPath,
              label: f.label
            }))}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            const nextSource: Doc<UserImportDataSourcePingboard> = {
              id: shortid(),
              collection: FS.userImportDataSources,
              data: {
                orgId: orgId,
                type: UserImportSourceType.PINGBOARD,
                status: UserImportDataSourceStatus.PENDING,
                detailedStatus: '',
                lastUpdate: null,
                createdBy: userId,
                createdAt: now(),
                customMappings: mappings.filter((m) => !!m.otherKey)
              }
            };
            await setDoc(nextSource);
            onClose();
          }}
        >
          {t('Add source')}
        </Button>
      </DialogActions>
    </>
  );
};

export const ImportSourceRowPingboard = ({
  d
}: {
  d: Doc<UserImportDataSourcePingboard>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={META.name}
      logoUrl={META.smallLogoUrl}
    />
  );
};
